import {
	CompositeLayerProps,
	Filter,
	MapboxLayerType,
} from '@Map/layers/types';
import { IconSet, LayerZindex, ServiceLayer } from '@Map/services/types';
import type VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';

import type { SymbolLayout } from '@Map/layers/mapbox/types';
import BubbleLayer from '@Map/layers/mapbox/BubbleLayer';
import FillLayer from '@Map/layers/mapbox/FillLayer';
import HeatmapLayer from '@Map/layers/mapbox/HeatmapLayer';
import HighlightLayer from '@Map/layers/mapbox/HighlightLayer';
import LineLayer from '@Map/layers/mapbox/LineLayer';
import MapboxLayer from '@Map/layers/mapbox/MapboxLayer';
import RasterLayer from '@Map/layers/mapbox/RasterLayer';
import SymbolLayer from '@Map/layers/mapbox/SymbolLayer';
import logger from '@Map/logger/Logger';
import { VectorTileStyleToEsriRenderer } from '@Map/esri/utils/VectorTileStyleToEsriRenderer';
import { setVectorTileLayerStyleLayerIfChanged } from '@Map/esri/utils/setVectorTileLayerStyleLayerIfChanged';
import { applySelectedIconImageSuffix } from '@Map/esri/utils/rendererHelpers';

export interface KVPNum {
	[key: string]: number | undefined;
}

/**
 * Minimal structural interface for the parent CompositeLayer that LayerBase
 * needs to reach back into. Avoids a circular import between LayerBase
 * (in creators/) and CompositeLayer (one directory up).
 */
export interface CompositeLike {
	findVectorTileLayer(styleLayerId: string): VectorTileLayer | null;
}

export interface Config {
	idPostfix?: string;
	zIndexesPerType?: KVPNum;
	selected?: boolean;
	highlightBackground?: boolean;
	highlightForeground?: boolean;
	composite?: CompositeLike;
}

export default abstract class LayerBase {
	private _layerInfo: CompositeLayerProps;
	protected _layer!: MapboxLayer;
	protected _secondLayer?: MapboxLayer;
	protected _vectorTileLayer: VectorTileLayer | undefined;
	protected _composite: CompositeLike | undefined;
	protected _isEsriMap = false;
	protected _idPostfix = '';
	protected _zIndexesPerType: KVPNum = {};
	protected _selected = false;
	protected _highlightBackground = false;
	protected _highlightForeground = false;
	protected _filter: Filter | undefined = undefined;

	constructor(
		layerInfo: CompositeLayerProps,
		vectorTileLayer: VectorTileLayer | undefined,
		config: Config = {},
	) {
		this._layerInfo = layerInfo;
		this._vectorTileLayer = vectorTileLayer;
		this._idPostfix = config.idPostfix ?? this._idPostfix;
		this._zIndexesPerType = config.zIndexesPerType ?? this._zIndexesPerType;
		this._selected = config.selected ?? this._selected;
		this._highlightBackground =
			config.highlightBackground ?? this._highlightBackground;
		this._highlightForeground =
			config.highlightForeground ?? this._highlightForeground;
		this._composite = config.composite ?? this._composite;
		this.setup();
	}

	get layerInfo(): CompositeLayerProps {
		return this._layerInfo;
	}

	get id(): CompositeLayerProps['id'] {
		return this.layerInfo.id + this._idPostfix;
	}

	get secondId(): CompositeLayerProps['id'] {
		return this.layerInfo.id + this._idPostfix + '-second';
	}

	get properties(): CompositeLayerProps['properties'] {
		return this.layerInfo.properties;
	}

	get hasClustering(): boolean {
		return this.layerInfo.cluster ?? false;
	}

	get layerType(): 'main' | 'selected' | 'highlight' {
		if (this._highlightBackground) return 'highlight';
		if (this._selected) return 'selected';
		return 'main';
	}

	set iconSet(iconSet: IconSet) {
		this._layer.iconSet = iconSet;
	}

	get serviceId(): CompositeLayerProps['serviceId'] {
		return this.layerInfo.serviceId;
	}

	get source(): string {
		if (
			this.layerInfo.source &&
			typeof this.layerInfo.source === 'string'
		) {
			return this.layerInfo.source;
		}
		return this.serviceId;
	}

	set vectorTileLayer(value: VectorTileLayer | undefined) {
		this._vectorTileLayer = value;
	}

	get sourceLayer(): CompositeLayerProps['sourceLayer'] {
		return this.layerInfo.sourceLayer;
	}

	set filter(filter: Filter | undefined) {
		this._filter = filter;
		this._resetFilterOnMap();
	}

	get filter(): Filter | undefined {
		return this._filter;
	}

	get onMap(): boolean {
		return true;
	}

	get styleLayer(): ServiceLayer {
		return this._layer.build();
	}

	/**
	 * The polygon outline (line) style layer built alongside the fill in
	 * setup(), or undefined for non-polygon layer types. Callers must push
	 * this onto the VectorTileLayer style themselves — id `secondId` — the
	 * same way `styleLayer` is pushed under `id`.
	 */
	get styleLayerSecond(): ServiceLayer | undefined {
		return this._secondLayer?.build();
	}

	get styleLayerSelected(): ServiceLayer {
		const styleLayer = { ...this._layer.build(true), filter: this.filter };
		if (styleLayer.type === 'symbol') {
			const layout = styleLayer.layout as SymbolLayout | undefined;
			if (layout?.['icon-image'] !== undefined) {
				styleLayer.layout = {
					...layout,
					'icon-image': applySelectedIconImageSuffix(
						layout['icon-image'],
						this._resolveSelectedColorForIconSuffix(),
					) as SymbolLayout['icon-image'],
				};
			}
		}
		return styleLayer;
	}

	/**
	 * Theme `selectedColor`, else last `inno:` sprite id in `selectedColorLookup`
	 * that encodes a selected tint (see {@link VectorTileStyleToEsriRenderer.parseIconImage}).
	 */
	private _resolveSelectedColorForIconSuffix(): string {
		const explicit = this.layerInfo.selectedColor;
		if (typeof explicit === 'string' && explicit.trim() !== '') {
			return explicit;
		}
		const fromLookup = LayerBase._selectedColorFromSelectedColorLookup(
			this.layerInfo.selectedColorLookup,
		);
		if (fromLookup) {
			return fromLookup;
		}
		return MapboxLayer.SELECTED_COLOR;
	}

	private static _selectedColorFromSelectedColorLookup(
		lookup: unknown[] | undefined,
	): string | undefined {
		if (!lookup?.length) {
			return undefined;
		}
		let last: string | undefined;
		const visit = (node: unknown): void => {
			if (typeof node === 'string' && node.includes('inno:')) {
				const { selectedColor } =
					VectorTileStyleToEsriRenderer.parseIconImage(node);
				if (selectedColor) {
					last = selectedColor;
				}
			} else if (Array.isArray(node)) {
				node.forEach(visit);
			}
		};
		lookup.forEach(visit);
		return last;
	}

	protected setup(): void {
		if (this.layerInfo.type === 'polygon') {
			this._layer = this.getLayerClass({
				...this._layerInfo,
				type: 'fill',
			});
			this._secondLayer = this.getLayerClass(
				{
					...this._layerInfo,
					color:
						this._layerInfo.outlineColor ??
						MapboxLayer.UNSELECTED_COLOR_OUTLINE,
					colorLookup: undefined,
					type: 'line',
					pattern: undefined,
				},
				true,
			);
		} else {
			this._layer = this.getLayerClass(this._layerInfo);
		}
	}

	draw(): void {}

	show(): void {
		this._setVisibility('visible');
	}

	hide(): void {
		this._setVisibility('none');
	}

	private _setVisibility(visibility: 'visible' | 'none'): void {
		const setOnLayer = (id: string) => {
			// Guard every Esri call with `.loaded` — getStyleLayerVisibility and
			// setStyleLayerVisibility both read internal `_styleObj.layers[id]`
			// and throw "Cannot read properties of undefined" when the style
			// spec hasn't loaded yet. Hits the cached path on initial load when
			// the VTL ref is set (e.g. from a fast source-match) but its style
			// hasn't arrived. Personal theme triggers this hard because it
			// hides many layers at boot, so the hide path fires before VTLs
			// have loaded.
			let vtl: VectorTileLayer | null | undefined = this._vectorTileLayer;
			let currentVisibility = vtl?.loaded
				? vtl.getStyleLayerVisibility(id)
				: undefined;
			if (currentVisibility == null) {
				vtl = this._composite?.findVectorTileLayer(id) ?? null;
				if (vtl?.loaded) {
					this._vectorTileLayer = vtl;
					currentVisibility = vtl.getStyleLayerVisibility(id);
				}
			}
			if (!vtl?.loaded) return;
			if (currentVisibility === visibility) return;
			vtl.setStyleLayerVisibility(id, visibility);
		};
		setOnLayer(this.id);
		if (this._secondLayer) {
			setOnLayer(this.secondId);
		}
	}

	delete(): void {}

	getLayerClass(
		layerInfo: CompositeLayerProps,
		secondLayer = false,
	): MapboxLayerType {
		const zIndexOffset =
			this._zIndexesPerType[layerInfo.type] ?? this._zIndexesPerType.base;
		const layerProps: CompositeLayerProps = {
			...layerInfo,
			id: secondLayer ? this.secondId : this.id,
			filter: this._getFilter(),
			selected: this._selected,
			highlight: this._highlightBackground,
			zIndex: zIndexOffset
				? this._getZIndex(
						layerInfo.zIndex,
						zIndexOffset,
						this.layerType,
					)
				: layerInfo.zIndex,
			minZoom: this._ignoreZoom() ? undefined : layerInfo.minZoom,
			maxZoom: this._ignoreZoom() ? undefined : layerInfo.maxZoom,
		};

		switch (layerProps.type) {
			case 'heatmap':
				return new HeatmapLayer({ ...layerProps, isEsri: true });
			case 'fill':
				return new FillLayer({ ...layerProps, isEsri: true });
			case 'line':
				return new LineLayer({ ...layerProps, isEsri: true });
			case 'raster':
				return new RasterLayer({ ...layerProps, isEsri: true });
			case 'bubble':
				return new BubbleLayer({ ...layerProps, isEsri: true });
			case 'symbol':
			default:
				return this._highlightBackground
					? new HighlightLayer({ ...layerProps, isEsri: true })
					: new SymbolLayer({ ...layerProps, isEsri: true });
		}
	}

	protected _ignoreZoom(): boolean {
		return this._highlightForeground || this._highlightBackground;
	}

	protected _getZIndex(
		zIndex: LayerZindex | undefined,
		offset = 0,
		layerType: 'main' | 'selected' | 'highlight' = 'main',
	): number {
		const fallback = 0 + offset;
		if (zIndex === undefined) return fallback;
		if (typeof zIndex === 'number') return zIndex + offset;
		if (layerType in zIndex && typeof zIndex[layerType] === 'number')
			return zIndex[layerType] ?? fallback;
		return fallback;
	}

	protected _getFilter = (): Filter | undefined => {
		return undefined;
	};

	protected _resetFilterOnMap = (secondLayer = false): void => {
		const layerId = secondLayer ? this.secondId : this.id;
		let vtl: VectorTileLayer | null | undefined = this._vectorTileLayer;
		let styleLayer = vtl?.loaded ? vtl.getStyleLayer(layerId) : undefined;
		if (!styleLayer) {
			vtl = this._composite?.findVectorTileLayer(layerId) ?? null;
			if (vtl?.loaded) {
				this._vectorTileLayer = vtl;
				styleLayer = vtl.getStyleLayer(layerId);
			}
		}
		if (!vtl?.loaded) {
			// The VectorTileLayer can be mid-(re)load exactly when a live
			// filter update (select/highlight/clear) fires. Retry once it
			// finishes loading instead of silently dropping the update.
			vtl?.when(() => this._resetFilterOnMap(secondLayer));
			return;
		}
		const filter = this._getFilter();
		if (styleLayer) {
			setVectorTileLayerStyleLayerIfChanged(
				vtl,
				{ ...styleLayer, filter },
				{ source: 'LayerBase._resetFilterOnMap' },
			);
		} else {
			logger.debug(`Layer ${layerId} not found in vectorTileLayer`);
		}
	};

	protected _resetState = (): void => {
		return;
	};
}
